import { useState } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useDownloadInstanceReport, DownloadInstanceReportValidation } from "@/lib/api/cfr";
import { downloadFromS3 } from "@/lib/file";
import { ResponseResult } from "@/lib/responseResults";
import { CommonDestinationTimezone } from "@/lib/utils";

interface DownloadReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DownloadReportModal({ open, onOpenChange }: DownloadReportModalProps) {
  const { toast } = useToast();
  const downloadInstanceReport = useDownloadInstanceReport();
  const [startDate, setStartDate] = useState<string>(moment.tz(CommonDestinationTimezone).subtract(7, "days").startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<string>(moment.tz(CommonDestinationTimezone).endOf("day").format("YYYY-MM-DD"));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [showFallbackLink, setShowFallbackLink] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!startDate || !endDate) {
      toast({
        title: "Error",
        description: "Start date and end date are required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setShowFallbackLink(false);
    setDownloadUrl(null);

    try {
      const validationResult = DownloadInstanceReportValidation.safeParse({
        created_start_date: startDate,
        created_end_date: endDate,
      });

      if (!validationResult.success) {
        toast({
          title: "Error",
          description: "Invalid date format. Please use YYYY-MM-DD format.",
          variant: "destructive",
        });
        return;
      }

      const apiResult = await downloadInstanceReport(validationResult.data);

      if (!apiResult.success) {
        throw apiResult;
      }

      const { presigned_get_url, document_id } = apiResult.data;

      // Attempt automatic download
      const downloadResult = await downloadFromS3(presigned_get_url, `cfr-instance-report_${validationResult.data.created_start_date}_${validationResult.data.created_end_date}.xlsx`);

        // Show fallback link if auto-download failed
        setDownloadUrl(presigned_get_url);
        setShowFallbackLink(true);
        toast({
          title: "Download Ready",
          description: "Click the link below to download the report",
        });
    } catch (error: any) {
      console.error("Failed to download report", error);
      const errorMessage = typeof error?.message === "string"
        ? error.message 
        : "Failed to download report";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStartDate("");
    setEndDate("");
    setDownloadUrl(null);
    setShowFallbackLink(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Download Instance Report</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="end_date">End Date *</Label>
              <Input
                id="end_date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                required
              />
            </div>

            {showFallbackLink && downloadUrl && (
              <div className="mt-2 p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground mb-2">
                  If the download did not start automatically:
                </p>
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Click here to download report
                </a>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Close
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Downloading..." : "Download Report"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

